/**
 * Arrow paging for the "latest works" gallery.
 *
 * The track is a plain overflow-x row — the arrows only drive its scroll
 * position, so trackpad, touch, and keyboard scrolling keep working exactly as
 * they did. Nothing here is load-bearing: with the script absent the row is
 * still scrollable, it just has no buttons.
 */

class GalleryNav {
  constructor(section) {
    this.track = section.querySelector('[data-gallery-track]');
    this.nav = section.querySelector('[data-gallery-nav]');
    this.prev = section.querySelector('[data-gallery-prev]');
    this.next = section.querySelector('[data-gallery-next]');
    if (!this.track || !this.nav) return;

    this.prev?.addEventListener('click', () => this.page(-1));
    this.next?.addEventListener('click', () => this.page(1));

    // Passive: this only reads scroll position, so it must never block scrolling.
    this.track.addEventListener('scroll', () => this.sync(), { passive: true });

    // Photos load lazily, so the track's width isn't final at parse time.
    // ResizeObserver catches both that and viewport changes.
    if ('ResizeObserver' in window) {
      this.observer = new ResizeObserver(() => this.sync());
      this.observer.observe(this.track);
    } else {
      window.addEventListener('resize', () => this.sync());
    }

    this.sync();
  }

  /** One item plus its gap — so a page lands cleanly on an image edge. */
  step() {
    const item = this.track.querySelector('.hiw-gallery__item');
    if (!item) return this.track.clientWidth;
    const gap = parseFloat(getComputedStyle(this.track).columnGap || '0') || 0;
    const width = item.getBoundingClientRect().width + gap;
    // Move a viewport at a time, rounded down to whole items, so nothing is
    // left half-visible at the edge.
    const perView = Math.max(1, Math.floor(this.track.clientWidth / width));
    return width * perView;
  }

  page(direction) {
    this.track.scrollBy({ left: this.step() * direction, behavior: 'smooth' });
  }

  sync() {
    const { scrollLeft, scrollWidth, clientWidth } = this.track;
    // A pixel of tolerance: fractional layout widths mean scrollLeft rarely
    // reaches the exact maximum, which would leave "next" enabled forever.
    const overflowing = scrollWidth - clientWidth > 1;

    this.nav.hidden = !overflowing;
    if (this.prev) this.prev.disabled = scrollLeft <= 1;
    if (this.next) this.next.disabled = scrollLeft >= scrollWidth - clientWidth - 1;
  }
}

for (const section of document.querySelectorAll('.hiw-gallery')) {
  new GalleryNav(section);
}
